import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import ButtonFilled from "../../../../components/FormElements/ButtonFilled";
import { useDispatch, useSelector } from "react-redux";
import offersActions from "../../../../store/offersStore/offersActions";
import { formatDate } from "../../../../helpers";
import Loader from "../../../../components/Loader";
import { useHistory } from "react-router-dom";
import styles from './offersList.module.scss';

const OffersList = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { info, pending: branchPending } = useSelector((s) => s.branchReducer);
  const { offers, pending } = useSelector((s) => s.offersReducer);
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [copiedOfferId, setCopiedOfferId] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (info && info.id) {
      dispatch(offersActions.getOffersForBranch(info.id));
    }
  }, [info?.id, i18n.language, dispatch]);

  const handleDelete = (offerId, e) => {
    e.stopPropagation();
    if (window.confirm(t('NSOffers.confirmDelete') || 'Вы уверены, что хотите удалить эту акцию?')) {
      dispatch(offersActions.deleteOffer(info.id, offerId, () => {
        dispatch(offersActions.getOffersForBranch(info.id));
      }));
    }
  };

  const handleActivate = (offerId, e) => {
    e.stopPropagation();
    dispatch(offersActions.activateOffer(info.id, offerId, () => {
      dispatch(offersActions.getOffersForBranch(info.id));
    }));
  };

  const handleSuspend = (offerId, e) => {
    e.stopPropagation();
    dispatch(offersActions.suspendOffer(info.id, offerId, () => {
      dispatch(offersActions.getOffersForBranch(info.id));
    }));
  };

  const handleShare = (offerId, e) => {
    e.stopPropagation();
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();

    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top
    });

    const currentHostname = window.location.hostname;
    const isProduction = currentHostname === 'business.sagibonus.com' || currentHostname === 'business.sagi.kz';
    const offersBaseUrl = isProduction
      ? 'https://offers.sagibonus.com/offers/offer'
      : 'https://test-offers.sagi.kz/offers/offer';
    const shareUrl = `${offersBaseUrl}/${offerId}`;

    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopiedOfferId(offerId);
      setTimeout(() => {
        setCopiedOfferId(null);
      }, 2000);
    }).catch((err) => {
      console.error('Failed to copy: ', err);
    });
  };

  const getOfferTypeLabel = (offerType) => {
    const types = {
      'percentage': t('NSOffers.percentage') || 'Процент',
      'fixed_amount': t('NSOffers.fixedAmount') || 'Фиксированная сумма',
      'buy_x_get_y': t('NSOffers.buyXGetY') || 'Купи X получи Y',
      'gift': t('NSOffers.gift') || 'Подарок'
    };
    return types[offerType] || offerType;
  };

  const formatOfferValue = (offer) => {
    if (offer.offer_type === 'percentage') {
      return `${offer.offer_value}%`;
    } else if (offer.offer_type === 'fixed_amount') {
      return `${offer.offer_value} ₸`;
    }
    return '-';
  };

  const getStatusLabel = (status) => {
    const statuses = {
      'waiting_activation': 'Ожидает активации',
      'active': 'Активный',
      'expired': 'Истекла',
      'suspended': 'Приостановлена'
    };
    return statuses[status] || status;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active':
        return styles.statusActive;
      case 'waiting_activation':
        return styles.statusWaiting;
      case 'expired':
        return styles.statusExpired;
      case 'suspended':
        return styles.statusSuspended;
      default:
        return '';
    }
  };

  return (
    <div className={styles.container}>
      <h1>
        <span>{t('NSSidebar.offers') || 'Акции'}</span>
        <ButtonFilled to="/offers/create">{t('NSOffers.createOffer') || 'Создать акцию'}</ButtonFilled>
      </h1>
      <div style={{ width: '100%' }}>
        <table className={`table ${styles.table}`}>
          <thead>
          <tr>
            <th>{t('title') || 'Название'}</th>
            <th>{t('NSOffers.type') || 'Тип'}</th>
            <th>{t('NSOffers.value') || 'Значение'}</th>
            <th>{t('period') || 'Период'}</th>
            <th>{t('NSOffers.promocode') || 'Промокод'}</th>
            <th>{t('status') || 'Статус'}</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
            {offers && offers.length > 0 ? offers.map((item) => (
              <tr key={item.id} onClick={() => history.push(`/offers/edit/${item.id}`)}>
                <td>
                  <div className={styles.titleCell}>
                    <strong>{item.title}</strong>
                    {item.description && <small>{item.description}</small>}
                  </div>
                </td>
                <td>{getOfferTypeLabel(item.offer_type)}</td>
                <td>{formatOfferValue(item)}</td>
                <td className={styles.period}>
                  {item.start_date && <span>{t("from") || 'от'} {formatDate(new Date(item.start_date), { year: 'numeric', month: 'numeric', day: 'numeric' })}</span>}
                  {item.end_date && <span>{t("till") || 'до'} {formatDate(new Date(item.end_date), { year: 'numeric', month: 'numeric', day: 'numeric' })}</span>}
                </td>
                <td>
                  {item.promocode_required ? (
                    <span className={styles.promocode}>{item.promocode || '-'}</span>
                  ) : (
                    <span className={styles.noPromocode}>{t('NSOffers.notRequired') || 'Не требуется'}</span>
                  )}
                </td>
                <td>
                  <div className={`${styles.status} ${getStatusClass(item.status)}`}>
                    <span>{getStatusLabel(item.status)}</span>
                  </div>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className={styles.actionButtons}>
                    <div className={styles.shareWrapper}>
                      <button
                        className={styles.shareBtn}
                        onClick={(e) => handleShare(item.id, e)}
                        title={t('NSOffers.share') || 'Поделиться'}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5.33333C13.1046 5.33333 14 4.4379 14 3.33333C14 2.22876 13.1046 1.33333 12 1.33333C10.8954 1.33333 10 2.22876 10 3.33333C10 4.4379 10.8954 5.33333 12 5.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M4 10C5.10457 10 6 9.10457 6 8C6 6.89543 5.10457 6 4 6C2.89543 6 2 6.89543 2 8C2 9.10457 2.89543 10 4 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 14.6667C13.1046 14.6667 14 13.7712 14 12.6667C14 11.5621 13.1046 10.6667 12 10.6667C10.8954 10.6667 10 11.5621 10 12.6667C10 13.7712 10.8954 14.6667 12 14.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M5.72656 8.94L10.2799 11.7267" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10.2732 4.27333L5.72656 7.06" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      {copiedOfferId === item.id && (
                        <span
                          className={styles.copiedTooltip}
                          style={{
                            left: `${tooltipPosition.x}px`,
                            top: `${tooltipPosition.y}px`
                          }}
                        >
                          Скопировано
                        </span>
                      )}
                    </div>
                    {item.status === 'active' ? (
                      <button
                        className={styles.suspendBtn}
                        onClick={(e) => handleSuspend(item.id, e)}
                      >
                        Приостановить
                      </button>
                    ) : (
                      <button
                        className={styles.activateBtn}
                        onClick={(e) => handleActivate(item.id, e)}
                      >
                        Активировать
                      </button>
                    )}
                    <button
                      className={styles.deleteBtn}
                      onClick={(e) => handleDelete(item.id, e)}
                    >
                      {t('delete') || 'Удалить'}
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                  {pending ? '' : (t('NSOffers.noOffers') || 'Нет акций')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {pending && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <Loader color="#73D389" style={{ margin: 'auto' }}/>
          </div>
        )}
      </div>
    </div>
  )
}

export default OffersList;
